import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

const SavLines = ({ lines, handleCreateOrUpdateSavOrder, disabled = false }) => {
  const handleRemoveItem = async (lineId) => {
    const newLines = lines?.filter((line) => lineId !== line._id);
    await handleCreateOrUpdateSavOrder(newLines);
  };

  const handleChangeQuantity = async (value, id) => {
    const newLines = lines?.map((line) =>
      line._id === id ? { ...line, quantity: value, isAllocated: null } : line
    );
    await handleCreateOrUpdateSavOrder(newLines);
  };

  if (lines?.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          mb: 1,
          mt: 1,
          p: 1,
          border: '1px solid rgb(19 64 123 / 24%)',
          color: 'rgb(19 64 123 / 44%)',
          width: '510px',
          borderRadius: '4px',
        }}
      >
        <Typography>Aucun produit</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
      {lines?.map((line) => (
        <Box
          key={line.reference}
          sx={{ display: 'flex', flexDirection: 'row' }}
        >
          <FormControl
            sx={{ width: '400px', marginRight: '10px' }}
            variant="outlined"
          >
            <OutlinedInput
              placeholder="Référence de la pièce ou du colis"
              type={'text'}
              size="small"
              disabled
              sx={{ color: 'black !important' }}
              value={line.internalProduct}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={'ajouter une pièce ou un colis'}
                    onClick={() => handleRemoveItem(line._id)}
                    edge="end"
                    disabled={disabled}
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          <FormControl variant="outlined" sx={{ width: '100px' }}>
            <TextField
              type="number"
              label="Quantité"
              disabled={disabled}
              value={line?.quantity || 1}
              onChange={(e) => {
                const value = e.target.value;
                // Validation pour s'assurer que la valeur reste entre 1 et 500
                if (value === '') {
                  handleChangeQuantity(1, line._id);
                  return;
                }
                const numValue = parseInt(value, 10);
                if (isNaN(numValue)) {
                  return; // Ignorer les valeurs non numériques
                }
                // Contraindre la valeur entre 1 et 500
                const constrainedValue = Math.min(Math.max(numValue, 1), 500);
                handleChangeQuantity(constrainedValue, line._id);
              }}
              size="small"
              sx={{ width: '100px' }}
              inputProps={{
                min: 1,
                max: 500,
                step: 1,
              }}
              InputProps={{
                'aria-label': 'Quantité',
              }}
            />
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {(() => {
              const requestedQuantity = line?.quantity || 1;
              const hasStockInfo = typeof line?.stockQuantity === 'number';
              const isVerifying =
                line?.isLoading || typeof line?.isValid !== 'boolean';

              // Vérification du stock en cours
              if (isVerifying) {
                return (
                  <Tooltip title="Vérification des stocks en cours...">
                    <CircularProgress
                      sx={{
                        width: '1.8em !important',
                        height: '1.8em !important',
                        marginLeft: '10px',
                      }}
                    />
                  </Tooltip>
                );
              }

              // Référence inexistante sur le BO
              if (line?.isValid === false) {
                return (
                  <Tooltip title="Référence produit invalide">
                    <ErrorIcon
                      color="error"
                      sx={{ fontSize: '1.8em', marginLeft: '10px' }}
                    />
                  </Tooltip>
                );
              }

              // En stock uniquement si le VirtualLevel couvre la quantité demandée
              const inStock =
                hasStockInfo && line.stockQuantity >= requestedQuantity;

              if (inStock) {
                return (
                  <Tooltip
                    title={`En stock (${line.stockQuantity} disponible(s))`}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: '1.8em', marginLeft: '10px' }}
                      color="success"
                    />
                  </Tooltip>
                );
              }

              // Stock insuffisant ou indisponible : décomposition nécessaire
              return (
                <Tooltip
                  title={
                    hasStockInfo
                      ? `Stock insuffisant : ${line.stockQuantity} en stock pour ${requestedQuantity} demandé(s). Une décomposition sera nécessaire.`
                      : 'Stock indisponible. Une décomposition sera nécessaire.'
                  }
                >
                  <CancelIcon
                    color="error"
                    sx={{ fontSize: '1.8em', marginLeft: '10px' }}
                  />
                </Tooltip>
              );
            })()}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SavLines;
